import { Trans } from '@lingui/macro';
import classnames from 'classnames';
import { FormikErrors } from 'formik';
import React, { FunctionComponent, useRef } from 'react';

import { FilestackPreviewImage } from '@components/common/filestack_uploader/FilestackPreviewImage';
import { FilestackUploader } from '@components/common/filestack_uploader/FilestackUploader';
import { UppyTusWrapper } from '@components/common/uppy/uppy_ucs/UppyTusWrapper';
import { TextWarningButton } from '@components/library/button';
import { CreateProjectValues } from '@components/show_page/sections/asset/custom-templates';
import { ValidationAlert } from '@components/show_page/sections/asset/custom-templates/ValidationAlert';
import { isUppyWithCompanionEnabled } from '@helpers/uppy';

import './styles/upload-an-image.scss';

interface UploadAnImageProps {
  backgroundImageUrl: string;
  errors: FormikErrors<CreateProjectValues>;
  filename: string;
  hasSubmitted: boolean;
  initialValues: CreateProjectValues;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  validateField: (field: string) => void;
}

export const UploadAnImage: FunctionComponent<UploadAnImageProps> = (props) => {
  const {
    backgroundImageUrl,
    errors,
    filename,
    hasSubmitted,
    initialValues,
    setFieldValue,
    setFieldTouched,
    validateField
  } = props;
  const triggerClickHandlerRef = useRef<((event: React.MouseEvent) => void) | null>(null);
  const uploadAreaId = 'custom-template-image-upload';

  const validate = (): void => {
    setTimeout(() => {
      validateField('backgroundImageUrl');
    }, 50);
  };

  return (
    <div
      className={classnames(
        'upload-an-image__upload',
        backgroundImageUrl && 'upload-an-image__upload--pending'
      )}
    >
      {!backgroundImageUrl
        ? (
          <>
            {hasSubmitted && errors.backgroundImageUrl && (
              <ValidationAlert>
                {errors.backgroundImageUrl}
              </ValidationAlert>
            )}
            {isUppyWithCompanionEnabled() ? (
              <>
                <div className="filestack-uploader-component">
                  <div className="filestack-upload-area-wrapper">
                    <div
                      className="filestack-upload-area companion-upload-trigger"
                      id={uploadAreaId}
                      onClick={(e): void => { if (triggerClickHandlerRef.current) triggerClickHandlerRef.current(e); }}
                      onKeyUp={(e): void => {
                        if (triggerClickHandlerRef.current) {
                          triggerClickHandlerRef.current(e as unknown as React.MouseEvent);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="fsp-drop-pane__container">
                        <div className="fsp-drop-pane__text">
                          <Trans>Drag file(s) here or click to browse</Trans>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <UppyTusWrapper
                  button
                  handleUpload={(files): void => {
                    if (!files.length) return;
                    const file = files[0];
                    setFieldValue('backgroundImageUrl', file.url);
                    setFieldTouched('backgroundImageUrl', true);
                    setFieldValue('filename', file.name);
                    validate();
                  }}
                  onTriggerClickHandler={triggerClickHandlerRef}
                  restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
                  trigger={`#${uploadAreaId}`}
                  uniqIdentifier="uppy-custom-template-image"
                />
              </>
            ) : (
              <FilestackUploader
                onUploadDone={(files): void => {
                  const filestackFile = files.filesUploaded[0];
                  setFieldValue('backgroundImageUrl', filestackFile.url);
                  setFieldTouched('backgroundImageUrl', true);
                  setFieldValue('filename', filestackFile.filename);
                  validate();
                }}
                pickerOptions={{
                  accept: 'image/*',
                  maxFiles: 1
                }}
              />
            )}
          </>
        )
        : (
          <figure className="upload-an-image__file">
            <FilestackPreviewImage
              alt={filename || ''}
              className="upload-an-image__file-image"
              src={backgroundImageUrl}
            />
            <div className="upload-an-image__file-details">
              {filename && (
                <figcaption className="upload-an-image__file-name">
                  {filename}
                </figcaption>
              )}
              <TextWarningButton
                id="upload-an-image-delete-image"
                onClick={(): void => {
                  setFieldValue('backgroundImageUrl', initialValues.backgroundImageUrl);
                  setFieldTouched('backgroundImageUrl', undefined);
                  setFieldValue('filename', initialValues.filename);
                  validate();
                }}
              >
                <Trans>Delete</Trans>
              </TextWarningButton>
            </div>
          </figure>
        )}
    </div>
  );
};
